import { create } from "zustand";

/**
 * 건물 상세 보기 탭 타입
 */
export type DetailTab = "info" | "tenant" | "demolish";

/**
 * 건물 상세 보기 상태 스토어
 * - activeTab: 현재 선택된 탭
 * - selectedBuildingId: 선택된 건물 ID (텍스트 위치 계산용)
 * - zipperProgress: 지퍼 슬라이더 진행도 (0~1)
 *
 * ExplodedBuildingModel에서 직접 참조하여 텍스트 모드 결정:
 * - "info" / "remodel" / "zip": 원래 positionU/V 위치
 * - "tenant": TenantListPanel 아래 정렬
 */
interface BuildingDetailStore {
  /** 현재 활성 탭 */
  activeTab: DetailTab;
  /** 탭 변경 */
  setActiveTab: (tab: DetailTab) => void;
  /** 선택된 건물 ID */
  selectedBuildingId: string | null;
  /** 건물 선택 */
  setSelectedBuilding: (id: string | null) => void;
  /** 지퍼 슬라이더 진행도 (0~1) */
  zipperProgress: number;
  /** 지퍼 진행도 변경 */
  setZipperProgress: (progress: number) => void;
  /** 상태 초기화 (건물 닫을 때) */
  reset: () => void;
}

export const useBuildingDetailStore = create<BuildingDetailStore>((set) => ({
  activeTab: "info",
  selectedBuildingId: null,
  zipperProgress: 0.5, // 중간에서 시작

  setActiveTab: (tab) => set({ activeTab: tab }),

  setSelectedBuilding: (id) =>
    set({
      selectedBuildingId: id,
      // 건물 선택 시 info 탭으로 리셋, 지퍼도 0.5로 리셋
      activeTab: "info",
      zipperProgress: 0.5,
    }),

  setZipperProgress: (progress) =>
    set((state) => ({
      zipperProgress: progress,
      // progress < 0.3 면 tenant 탭 (폭발 애니메이션)
      // progress >= 0.3 면 원래 탭 유지
      activeTab:
        progress < 0.05
          ? "tenant"
          : state.activeTab === "tenant"
          ? "info"
          : state.activeTab,
    })),

  reset: () =>
    set({
      activeTab: "info",
      selectedBuildingId: null,
      zipperProgress: 0.5,
    }),
}));

/**
 * 텍스트 위치 모드 계산 훅
 * activeTab에 따라 "wall" 또는 "panel" 반환
 */
export function useTextPositionMode(): "wall" | "panel" {
  const activeTab = useBuildingDetailStore((state) => state.activeTab);

  // tenant 탭일 때만 패널 모드, 나머지는 벽면 모드
  return activeTab === "tenant" ? "panel" : "wall";
}
